import { useContext } from "react";
import ThemeContext from "../contexts/ThemeContext";

/**
 * Custom hook to access theme values throughout the application
 * @returns {Object} Theme object with all colors, typography, spacing, etc.
 *
 * @example
 * const theme = useTheme();
 *
 * // Access colors
 * const primaryColor = theme.colors.primary[500];
 * const backgroundColor = theme.colors.neutral[50];
 *
 * // Access typography
 * const fontFamily = theme.typography.fonts.primary;
 * const fontSize = theme.typography.fontSizes.lg;
 *
 * // Access spacing
 * const padding = theme.spacing[4];
 * const margin = theme.spacing[8];
 *
 * // Access border radius
 * const borderRadius = theme.borderRadius.lg;
 *
 * // Access shadows
 * const boxShadow = theme.shadows.md;
 */
export const useTheme = () => {
  const theme = useContext(ThemeContext);

  if (!theme) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }

  return theme;
};

/**
 * Helper function to get color with opacity
 * @param {string} color - The color value (e.g., '#9C001A')
 * @param {number} opacity - Opacity value between 0 and 1
 * @returns {string} Color with opacity
 *
 * @example
 * const theme = useTheme();
 * const primaryColorWithOpacity = getColorWithOpacity(theme.colors.primary[500], 0.8);
 */
export const getColorWithOpacity = (color, opacity) => {
  // Convert hex to rgba
  if (color.startsWith("#")) {
    const hex = color.replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  // If it's already rgba or rgb, try to extract and modify
  if (color.startsWith("rgb")) {
    return color.replace(/[\d.]+\)$/g, `${opacity})`);
  }

  return color;
};

/**
 * Helper function to create responsive styles
 * @param {Object} styles - Object with breakpoint keys and style values
 * @returns {Object} Responsive styles object
 *
 * @example
 * const theme = useTheme();
 * const responsiveStyles = createResponsiveStyles({
 *   mobile: { fontSize: theme.typography.fontSizes.sm },
 *   tablet: { fontSize: theme.typography.fontSizes.base },
 *   desktop: { fontSize: theme.typography.fontSizes.lg }
 * });
 */
export const createResponsiveStyles = (styles) => {
  return styles;
};

/**
 * Helper function to create consistent spacing
 * @param {string|number} value - Spacing value or key
 * @returns {string} Spacing value
 *
 * @example
 * const theme = useTheme();
 * const padding = getSpacing(theme, 4); // Returns '1rem'
 * const margin = getSpacing(theme, '8'); // Returns '2rem'
 */
export const getSpacing = (theme, value) => {
  if (
    typeof value === "number" ||
    (typeof value === "string" && !isNaN(value))
  ) {
    return theme.spacing[value] || `${value}rem`;
  }
  return value;
};

export default useTheme;
